import {
  FEDERAL_PAYCHECK_TAX_CONFIG,
  INCOME_TYPES,
} from '../config/federalPaycheckTaxConstants';

export { INCOME_TYPES };

function roundCents(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function nonNeg(n) {
  return Math.max(0, Number(n) || 0);
}

function resolveFilingBracketKey(filingStatus) {
  return filingStatus === 'married_filing_jointly'
    ? 'married_filing_jointly'
    : 'single';
}

function resolveAdditionalMedicareThreshold(filingStatus) {
  const thresholds = FEDERAL_PAYCHECK_TAX_CONFIG.additionalMedicareWageThresholds;
  if (filingStatus === 'married_filing_jointly') return thresholds.married_filing_jointly;
  if (filingStatus === 'married_filing_separately') return thresholds.married_filing_separately;
  if (filingStatus === 'head_of_household') return thresholds.head_of_household;
  if (filingStatus === 'qualifying_surviving_spouse') return thresholds.qualifying_surviving_spouse;
  return thresholds.single;
}

function resolveStandardDeduction(filingStatus) {
  const std = FEDERAL_PAYCHECK_TAX_CONFIG.standardDeduction;
  if (filingStatus === 'married_filing_jointly') return std.married_filing_jointly;
  if (filingStatus === 'head_of_household') return std.head_of_household;
  if (filingStatus === 'married_filing_separately') return std.married_filing_separately;
  if (filingStatus === 'qualifying_surviving_spouse') return std.qualifying_surviving_spouse;
  return std.single;
}

function earnerHasW2(incomeType) {
  return incomeType === INCOME_TYPES.W2 || incomeType === INCOME_TYPES.MIXED;
}

function earnerHasSe(incomeType) {
  return incomeType === INCOME_TYPES.SELF_EMPLOYED || incomeType === INCOME_TYPES.MIXED;
}

/**
 * Progressive federal income tax on taxable ordinary income (standard deduction already applied).
 */
export function computeFederalIncomeTax(taxableIncome, filingStatus) {
  const income = Math.max(0, Number(taxableIncome) || 0);
  const key = resolveFilingBracketKey(filingStatus);
  const brackets = FEDERAL_PAYCHECK_TAX_CONFIG.federalIncomeBrackets[key];

  let tax = 0;
  let priorCap = 0;

  for (const { upTo, rate } of brackets) {
    const cap = upTo === Infinity ? Infinity : upTo;
    if (income <= priorCap) break;
    const bracketTop = Math.min(income, cap);
    const amountInBracket = bracketTop - priorCap;
    if (amountInBracket > 0) {
      tax += amountInBracket * rate;
    }
    priorCap = cap;
    if (cap === Infinity) break;
  }

  return roundCents(tax);
}

/** Top marginal federal bracket rate for taxable ordinary income. */
export function computeMarginalFederalRate(taxableIncome, filingStatus) {
  const income = Math.max(0, Number(taxableIncome) || 0);
  const key = resolveFilingBracketKey(filingStatus);
  const brackets = FEDERAL_PAYCHECK_TAX_CONFIG.federalIncomeBrackets[key];

  for (const { upTo, rate } of brackets) {
    if (income <= upTo) return rate;
  }
  return brackets[brackets.length - 1].rate;
}

/** How federal income tax stacks across progressive brackets (after standard deduction). */
export function computeFederalTaxBracketBreakdown(taxableIncome, filingStatus) {
  return buildFederalTaxLadder(taxableIncome, filingStatus).filledRows;
}

/**
 * Full tax-ladder model for visualization.
 *
 * `applicableSteps` — brackets with amountInBracket > 0 only, ordered lowest rate → highest
 * (the user’s marginal bracket is always last). Empty / unreached brackets are omitted.
 *
 * Progress bars (`fillRatio`):
 * - Finite brackets: amount taxed in bracket ÷ bracket width (0–1).
 * - Top open-ended bracket (no upper bound): always shown as fully filled (1) when reached,
 *   because there is no finite width to measure against.
 *
 * @param {number} taxableIncome
 * @param {'single'|'married_filing_jointly'|string} filingStatus
 * @param {object} [opts]
 * @param {number} [opts.earnedIncomeForSurcharge] — Medicare wages + SE net earnings (not taxable income)
 */
export function buildFederalTaxLadder(taxableIncome, filingStatus, opts = {}) {
  const income = Math.max(0, Number(taxableIncome) || 0);
  const key = resolveFilingBracketKey(filingStatus);
  const brackets = FEDERAL_PAYCHECK_TAX_CONFIG.federalIncomeBrackets[key];
  const addMedThreshold = resolveAdditionalMedicareThreshold(filingStatus);
  const niitThreshold = resolveNiitThreshold(filingStatus);
  const earned = Math.max(0, Number(opts.earnedIncomeForSurcharge) || 0);

  const steps = [];
  const filledRows = [];
  let priorCap = 0;
  let cumulativeTax = 0;

  for (const { upTo, rate } of brackets) {
    const isTop = upTo === Infinity;
    const cap = isTop ? Infinity : upTo;
    const width = isTop ? null : cap - priorCap;
    const maxTaxInBracket = width == null ? null : roundCents(width * rate);

    let amountInBracket = 0;
    let fillRatio = 0;
    let status = 'empty';

    if (income > priorCap) {
      const bracketTop = Math.min(income, isTop ? income : cap);
      amountInBracket = roundCents(bracketTop - priorCap);
      if (isTop) {
        // Open-ended top bracket: no finite width — show as fully filled when reached.
        fillRatio = amountInBracket > 0 ? 1 : 0;
        status = amountInBracket > 0 ? 'marginal' : 'empty';
      } else if (income >= cap) {
        fillRatio = 1;
        status = 'full';
      } else {
        fillRatio = width > 0 ? amountInBracket / width : 0;
        status = 'marginal';
      }
    }

    const taxInBracket = roundCents(amountInBracket * rate);
    cumulativeTax = roundCents(cumulativeTax + taxInBracket);

    const step = {
      from: priorCap,
      to: isTop ? null : cap,
      rate,
      amountInBracket,
      taxInBracket,
      maxTaxInBracket,
      fillRatio,
      status,
      isMarginal: status === 'marginal',
      isTop,
      rangeLabel: isTop
        ? `${formatUsdCompact(priorCap)}+`
        : `${formatUsdCompact(priorCap)} – ${formatUsdCompact(cap)}`,
    };
    steps.push(step);

    if (amountInBracket > 0) {
      filledRows.push({
        rate,
        amountInBracket,
        taxInBracket,
        isMarginal: step.isMarginal,
        from: step.from,
        to: step.to,
        maxTaxInBracket,
        fillRatio: step.fillRatio,
        status: step.status,
        isTop: step.isTop,
        rangeLabel: step.rangeLabel,
      });
    }

    priorCap = cap;
    if (isTop) break;
  }

  /** Only brackets with taxable income > 0, lowest rate → highest (marginal last). */
  const applicableSteps = steps.filter((s) => s.amountInBracket > 0);

  const lastFinite = [...steps].reverse().find((s) => s.to != null);
  const displayCeiling = lastFinite ? Math.round(lastFinite.to * 1.2) : 1_000_000;

  const markerPosition = (dollarAmount) => {
    const amt = Math.max(0, Number(dollarAmount) || 0);
    const n = steps.length;
    for (let i = 0; i < n; i += 1) {
      const step = steps[i];
      const top = step.to == null ? displayCeiling : step.to;
      if (amt <= top || i === n - 1) {
        const span = Math.max(1, top - step.from);
        const within = Math.min(1, Math.max(0, (amt - step.from) / span));
        return (i + within) / n;
      }
    }
    return 1;
  };

  const taxableIncomePosition = markerPosition(Math.min(income, displayCeiling));
  const taxFromApplicable = roundCents(
    applicableSteps.reduce((sum, s) => sum + s.taxInBracket, 0),
  );

  return {
    taxYear: FEDERAL_PAYCHECK_TAX_CONFIG.taxYear,
    filingStatus,
    taxableIncome: income,
    /** Estimated ordinary federal income tax before credits. */
    totalFederalIncomeTax: cumulativeTax,
    taxFromApplicableSteps: taxFromApplicable,
    marginalRate: computeMarginalFederalRate(income, filingStatus),
    effectiveRateOnTaxable: income > 0 ? cumulativeTax / income : 0,
    steps,
    /** @deprecated Prefer applicableSteps — same filter, ordered low → high. */
    filledRows,
    applicableSteps,
    displayCeiling,
    taxableIncomePosition,
    thresholds: {
      additionalMedicare: {
        key: 'additional_medicare',
        label: 'Additional Medicare Tax',
        shortLabel: 'Medicare surcharge',
        amount: addMedThreshold,
        rate: FEDERAL_PAYCHECK_TAX_CONFIG.medicare.additionalRate,
        note: '0.9% on earned income (W-2 Medicare wages + SE earnings) above this threshold — not on taxable income.',
        position: markerPosition(addMedThreshold),
        crossed: earned > addMedThreshold,
        earnedIncome: earned,
        excess: Math.max(0, earned - addMedThreshold),
      },
      niit: {
        key: 'niit',
        label: 'NIIT / taxable-account surcharge',
        shortLabel: 'NIIT',
        amount: niitThreshold,
        rate: FEDERAL_PAYCHECK_TAX_CONFIG.niitRate,
        note: '3.8% on net investment income when MAGI exceeds this threshold. Shown for planning context only — this calculator does not estimate NIIT.',
        position: markerPosition(niitThreshold),
        crossed: false,
      },
    },
  };
}

function resolveNiitThreshold(filingStatus) {
  const thresholds = FEDERAL_PAYCHECK_TAX_CONFIG.niitMagiThresholds;
  if (filingStatus === 'married_filing_jointly') return thresholds.married_filing_jointly;
  if (filingStatus === 'married_filing_separately') return thresholds.married_filing_separately;
  if (filingStatus === 'head_of_household') return thresholds.head_of_household;
  if (filingStatus === 'qualifying_surviving_spouse') return thresholds.qualifying_surviving_spouse;
  return thresholds.single;
}

function formatUsdCompact(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Employee OASDI on one person's wages (wage base applied per worker). */
export function computeSocialSecurityOnWages(wages) {
  const base = FEDERAL_PAYCHECK_TAX_CONFIG.socialSecurity;
  const w = nonNeg(wages);
  return roundCents(base.employeeRate * Math.min(w, base.annualWageBase));
}

/** Employee Medicare 1.45% on one person's wages (no wage ceiling). */
export function computeRegularMedicareOnWages(wages) {
  const rate = FEDERAL_PAYCHECK_TAX_CONFIG.medicare.employeeRate;
  return roundCents(rate * nonNeg(wages));
}

/**
 * Net self-employment profit = max(0, gross revenue − deductible business expenses).
 * Never treat gross 1099 receipts as SE taxable profit.
 */
export function computeNetSelfEmploymentProfit(grossRevenue, businessExpenses) {
  return roundCents(Math.max(0, nonNeg(grossRevenue) - nonNeg(businessExpenses)));
}

/**
 * Schedule SE net earnings subject to SE tax (profit × 92.35%).
 * Returns 0 when profit is ≤ 0 or net earnings are below the filing threshold.
 */
export function computeSeNetEarningsSubjectToTax(netProfit) {
  const profit = nonNeg(netProfit);
  if (profit <= 0) return 0;
  const { netEarningsFactor, filingThreshold } = FEDERAL_PAYCHECK_TAX_CONFIG.selfEmployment;
  const netEarnings = roundCents(profit * netEarningsFactor);
  if (netEarnings < filingThreshold) return 0;
  return netEarnings;
}

/**
 * Self-employment tax for one individual, coordinating the SS wage base with that
 * person's own W-2 Social Security wages.
 *
 * @param {object} p
 * @param {number} p.netProfit — net SE profit (gross − expenses), not gross receipts
 * @param {number} [p.w2SocialSecurityWages] — that individual's W-2 SS wages
 */
export function computeSelfEmploymentTax({ netProfit, w2SocialSecurityWages = 0 }) {
  const cfg = FEDERAL_PAYCHECK_TAX_CONFIG;
  const netEarnings = computeSeNetEarningsSubjectToTax(netProfit);
  const wageBase = cfg.socialSecurity.annualWageBase;
  const remainingSsBase = Math.max(0, wageBase - nonNeg(w2SocialSecurityWages));
  const ssSeTaxBase = Math.min(netEarnings, remainingSsBase);
  const socialSecurityPortion = roundCents(ssSeTaxBase * cfg.socialSecurity.selfEmploymentRate);
  const medicarePortion = roundCents(netEarnings * cfg.medicare.selfEmploymentRate);
  const baseSelfEmploymentTax = roundCents(socialSecurityPortion + medicarePortion);
  const deductiblePortion = roundCents(
    baseSelfEmploymentTax * cfg.selfEmployment.deductiblePortionRate,
  );

  return {
    netProfit: nonNeg(netProfit),
    netEarningsSubjectToSeTax: netEarnings,
    remainingSocialSecurityWageBase: remainingSsBase,
    socialSecuritySeTaxBase: ssSeTaxBase,
    socialSecurityPortion,
    medicarePortion,
    /** Base SE tax before household Additional Medicare Tax. */
    baseSelfEmploymentTax,
    deductiblePortion,
    owesSelfEmploymentTax: netEarnings > 0,
  };
}

/**
 * Additional Medicare Tax on combined Medicare wages + SE net earnings above threshold.
 * Does not include the 1.45%/2.9% regular Medicare already computed separately.
 */
export function computeAdditionalMedicareTax({
  filingStatus,
  medicareWages = 0,
  seNetEarningsSubjectToTax = 0,
}) {
  const rate = FEDERAL_PAYCHECK_TAX_CONFIG.medicare.additionalRate;
  const threshold = resolveAdditionalMedicareThreshold(filingStatus);
  const combined = nonNeg(medicareWages) + nonNeg(seNetEarningsSubjectToTax);
  const excess = Math.max(0, combined - threshold);
  if (excess <= 0) return 0;
  return roundCents(rate * excess);
}

export function computeEmployeeSocialSecurityAnnual({
  filingStatus,
  primaryAnnualW2,
  spouseAnnualW2,
  bonusSupplemental,
}) {
  const primaryWages = nonNeg(primaryAnnualW2) + nonNeg(bonusSupplemental);
  const spouseWages = nonNeg(spouseAnnualW2);

  if (filingStatus === 'married_filing_jointly') {
    return roundCents(
      computeSocialSecurityOnWages(primaryWages) + computeSocialSecurityOnWages(spouseWages),
    );
  }

  return computeSocialSecurityOnWages(primaryWages);
}

export function computeMedicareAnnual({
  filingStatus,
  primaryAnnualW2,
  spouseAnnualW2,
  bonusSupplemental,
}) {
  const primaryWages = nonNeg(primaryAnnualW2) + nonNeg(bonusSupplemental);
  const spouseWages = nonNeg(spouseAnnualW2);

  if (filingStatus === 'married_filing_jointly') {
    return roundCents(
      computeRegularMedicareOnWages(primaryWages) + computeRegularMedicareOnWages(spouseWages),
    );
  }

  return computeRegularMedicareOnWages(primaryWages);
}

/** @deprecated Prefer computeAdditionalMedicareTax with explicit SE earnings. */
export function computeAdditionalMedicareAnnual({
  filingStatus,
  primaryAnnualW2,
  spouseAnnualW2,
  bonusSupplemental,
  seNetEarningsSubjectToTax = 0,
}) {
  const medicareWages =
    nonNeg(primaryAnnualW2) + nonNeg(spouseAnnualW2) + nonNeg(bonusSupplemental);
  return computeAdditionalMedicareTax({
    filingStatus,
    medicareWages,
    seNetEarningsSubjectToTax,
  });
}

/**
 * Split a household dollar amount across spouses by wage / income share.
 * Ensures parts sum to `total` after rounding (remainder to primary).
 */
export function splitByWageShare(total, primaryWages, spouseWages) {
  const p = nonNeg(primaryWages);
  const s = nonNeg(spouseWages);
  const denom = p + s;
  const t = roundCents(total);
  if (denom <= 0) {
    return { primaryPart: t, spousePart: 0, primaryShare: 1, spouseShare: 0 };
  }
  const primaryShare = p / denom;
  const spouseShare = s / denom;
  const primaryPart = roundCents(t * primaryShare);
  const spousePart = roundCents(t - primaryPart);
  return { primaryPart, spousePart, primaryShare, spouseShare };
}

function normalizeIncomeType(value) {
  if (value === INCOME_TYPES.SELF_EMPLOYED || value === INCOME_TYPES.MIXED) return value;
  return INCOME_TYPES.W2;
}

function resolveHouseholdIncomeType(primaryType, spouseType, isMarried) {
  const types = [primaryType];
  if (isMarried) types.push(spouseType);
  const hasW2 = types.some(earnerHasW2);
  const hasSe = types.some(earnerHasSe);
  if (hasW2 && hasSe) return INCOME_TYPES.MIXED;
  if (hasSe) return INCOME_TYPES.SELF_EMPLOYED;
  return INCOME_TYPES.W2;
}

/**
 * Full federal tax estimate with FIT separated from payroll / SE taxes.
 *
 * @param {object} p
 * @param {'single'|'married_filing_jointly'} p.filingStatus
 * @param {'w2'|'self_employed'|'mixed'} [p.primaryIncomeType]
 * @param {'w2'|'self_employed'|'mixed'} [p.spouseIncomeType]
 * @param {number} [p.primaryAnnualW2]
 * @param {number} [p.spouseAnnualW2]
 * @param {number} [p.bonusSupplemental]
 * @param {number} [p.primarySeGross]
 * @param {number} [p.primarySeExpenses]
 * @param {number} [p.spouseSeGross]
 * @param {number} [p.spouseSeExpenses]
 * @param {number} [p.federalIncomeTaxWithheld] — FIT already withheld from W-2 pay
 * @param {number} [p.estimatedPaymentsMade] — quarterly estimated FIT / SE payments already made
 * @param {number} [p.otherWithholding] — optional other FIT withholding
 * @param {number} [p.primaryPayPeriodsPerYear]
 * @param {number} [p.spousePayPeriodsPerYear]
 */
export function computeFullPaycheckWithholdingEstimate({
  filingStatus,
  primaryIncomeType = INCOME_TYPES.W2,
  spouseIncomeType = INCOME_TYPES.W2,
  primaryAnnualW2 = 0,
  spouseAnnualW2 = 0,
  bonusSupplemental = 0,
  primarySeGross = 0,
  primarySeExpenses = 0,
  spouseSeGross = 0,
  spouseSeExpenses = 0,
  federalIncomeTaxWithheld = 0,
  estimatedPaymentsMade = 0,
  otherWithholding = 0,
  primaryPayPeriodsPerYear,
  spousePayPeriodsPerYear,
}) {
  const isMarried = filingStatus === 'married_filing_jointly';
  const primaryType = normalizeIncomeType(primaryIncomeType);
  const spouseType = isMarried ? normalizeIncomeType(spouseIncomeType) : INCOME_TYPES.W2;
  const householdIncomeType = resolveHouseholdIncomeType(primaryType, spouseType, isMarried);

  const primaryW2Base = earnerHasW2(primaryType) ? nonNeg(primaryAnnualW2) : 0;
  const bonus = earnerHasW2(primaryType) ? nonNeg(bonusSupplemental) : 0;
  const pWages = primaryW2Base + bonus;
  const sWages = isMarried && earnerHasW2(spouseType) ? nonNeg(spouseAnnualW2) : 0;

  const primarySeProfit = earnerHasSe(primaryType)
    ? computeNetSelfEmploymentProfit(primarySeGross, primarySeExpenses)
    : 0;
  const spouseSeProfit = isMarried && earnerHasSe(spouseType)
    ? computeNetSelfEmploymentProfit(spouseSeGross, spouseSeExpenses)
    : 0;

  const primarySe = computeSelfEmploymentTax({
    netProfit: primarySeProfit,
    w2SocialSecurityWages: pWages,
  });
  const spouseSe = computeSelfEmploymentTax({
    netProfit: spouseSeProfit,
    w2SocialSecurityWages: sWages,
  });

  const employeeSsPrimary = pWages > 0 ? computeSocialSecurityOnWages(pWages) : 0;
  const employeeSsSpouse = sWages > 0 ? computeSocialSecurityOnWages(sWages) : 0;
  const employeeMedPrimary = pWages > 0 ? computeRegularMedicareOnWages(pWages) : 0;
  const employeeMedSpouse = sWages > 0 ? computeRegularMedicareOnWages(sWages) : 0;

  const socialSecurityTax = roundCents(employeeSsPrimary + employeeSsSpouse);
  const medicareTax = roundCents(employeeMedPrimary + employeeMedSpouse);

  const totalSeNetEarnings =
    primarySe.netEarningsSubjectToSeTax + spouseSe.netEarningsSubjectToSeTax;
  const medicareWagesCombined = pWages + sWages;
  const additionalMedicareTax = computeAdditionalMedicareTax({
    filingStatus,
    medicareWages: medicareWagesCombined,
    seNetEarningsSubjectToTax: totalSeNetEarnings,
  });

  const baseSelfEmploymentTax = roundCents(
    primarySe.baseSelfEmploymentTax + spouseSe.baseSelfEmploymentTax,
  );
  const seDeductiblePortion = roundCents(
    primarySe.deductiblePortion + spouseSe.deductiblePortion,
  );
  /** Total SE tax owed includes that individual's share of household Additional Medicare when SE applies. */
  const selfEmploymentTaxOwed = roundCents(baseSelfEmploymentTax);

  const totalW2Wages = pWages + sWages;
  const totalSeProfit = roundCents(primarySeProfit + spouseSeProfit);
  const grossHouseholdIncome = roundCents(totalW2Wages + totalSeProfit);

  const std = resolveStandardDeduction(filingStatus);
  const adjustedGrossIncome = Math.max(
    0,
    roundCents(totalW2Wages + totalSeProfit - seDeductiblePortion),
  );
  const taxableIncome = Math.max(0, roundCents(adjustedGrossIncome - std));

  const federalIncomeTax = computeFederalIncomeTax(taxableIncome, filingStatus);

  const withheldFit = nonNeg(federalIncomeTaxWithheld) + nonNeg(otherWithholding);
  const estimatedPaid = nonNeg(estimatedPaymentsMade);
  const paymentsAndWithholding = roundCents(withheldFit + estimatedPaid);
  const remainingFitToCover = roundCents(federalIncomeTax - paymentsAndWithholding);

  const totalEmployeePayrollTaxes = roundCents(
    socialSecurityTax + medicareTax + additionalMedicareTax,
  );
  const totalEstimatedFederalTaxes = roundCents(
    federalIncomeTax + socialSecurityTax + medicareTax + additionalMedicareTax + selfEmploymentTaxOwed,
  );

  /**
   * Amount still to cover via FIT withholding / estimated payments.
   * For SE / mixed households this includes SE tax (1099 payers generally do not withhold it).
   * Employee FICA is already taken from W-2 paychecks and is not added here.
   */
  const amountAlreadyCoveredTowardSetAside = paymentsAndWithholding;
  const planningSetAsideTarget = householdIncomeType === INCOME_TYPES.W2
    ? federalIncomeTax
    : roundCents(federalIncomeTax + selfEmploymentTaxOwed);
  const remainingAmountToCover = roundCents(planningSetAsideTarget - amountAlreadyCoveredTowardSetAside);

  let resultType = 'approximately_on_track';
  if (remainingAmountToCover > 500) resultType = 'projected_due';
  else if (remainingAmountToCover < -500) resultType = 'projected_overpayment';

  const primaryPeriods = Math.max(1, Number(primaryPayPeriodsPerYear) || 1);
  const spousePeriods = isMarried
    ? Math.max(1, Number(spousePayPeriodsPerYear ?? primaryPayPeriodsPerYear) || 1)
    : primaryPeriods;

  const allocFit = splitByWageShare(federalIncomeTax, pWages || primarySeProfit, sWages || spouseSeProfit);
  const allocAddMed = splitByWageShare(additionalMedicareTax, pWages || primarySeProfit, sWages || spouseSeProfit);

  const householdTaxRates = computeEarnerTaxRates({
    grossWagesAnnual: grossHouseholdIncome,
    taxableIncomeAnnual: taxableIncome,
    federalIncomeTaxAnnual: federalIncomeTax,
    socialSecurityAnnual: socialSecurityTax,
    medicareAnnual: medicareTax,
    additionalMedicareAnnual: additionalMedicareTax,
    selfEmploymentTaxAnnual: selfEmploymentTaxOwed,
    filingStatus,
  });

  const buildEarnerRow = ({
    key,
    title,
    subtitle,
    incomeType,
    grossWagesAnnual,
    seProfit,
    seDetail,
    wageShareOfHousehold,
    federalIncomeTaxAnnual: fitAnnual,
    socialSecurityAnnual: ssAnnual,
    medicareAnnual: medAnnual,
    additionalMedicareAnnual: addMedAnnual,
    payPeriodsPerYear,
  }) => {
    const employeePayrollAnnual = roundCents(ssAnnual + medAnnual + addMedAnnual);
    const fitPerPaycheck = roundCents(fitAnnual / payPeriodsPerYear);
    const perPaycheck = computeEarnerPerPaycheckBreakdown({
      federalIncomeTaxAnnual: fitAnnual,
      socialSecurityAnnual: ssAnnual,
      medicareAnnual: medAnnual,
      additionalMedicareAnnual: addMedAnnual,
      payPeriodsPerYear,
    });
    const earnerPayrollRates = computeEarnerTaxRates({
      grossWagesAnnual: grossWagesAnnual + seProfit,
      federalIncomeTaxAnnual: fitAnnual,
      socialSecurityAnnual: ssAnnual,
      medicareAnnual: medAnnual,
      additionalMedicareAnnual: addMedAnnual,
      selfEmploymentTaxAnnual: seDetail?.baseSelfEmploymentTax || 0,
      filingStatus,
    });
    const taxRates = isMarried
      ? {
          ...householdTaxRates,
          effectiveSocialSecurityRate: earnerPayrollRates.effectiveSocialSecurityRate,
          effectiveRegularMedicareRate: earnerPayrollRates.effectiveRegularMedicareRate,
          effectiveMedicareRate: earnerPayrollRates.effectiveMedicareRate,
          socialSecurityAtWageCap: earnerPayrollRates.socialSecurityAtWageCap,
        }
      : computeEarnerTaxRates({
          grossWagesAnnual: grossWagesAnnual + seProfit,
          taxableIncomeAnnual: taxableIncome,
          federalIncomeTaxAnnual: fitAnnual,
          socialSecurityAnnual: ssAnnual,
          medicareAnnual: medAnnual,
          additionalMedicareAnnual: addMedAnnual,
          selfEmploymentTaxAnnual: seDetail?.baseSelfEmploymentTax || 0,
          filingStatus,
        });

    return {
      key,
      title,
      subtitle,
      incomeType,
      hasW2: earnerHasW2(incomeType),
      hasSelfEmployment: earnerHasSe(incomeType),
      grossWagesAnnual,
      seProfit,
      seDetail,
      wageShareOfHousehold,
      federalIncomeTaxAnnual: fitAnnual,
      socialSecurityAnnual: ssAnnual,
      medicareAnnual: medAnnual,
      additionalMedicareAnnual: addMedAnnual,
      employeePayrollTaxAnnual: employeePayrollAnnual,
      ficaTaxAnnual: employeePayrollAnnual,
      /** FIT-only annual withholding recommendation for this earner. */
      recommendedFederalIncomeTaxWithholdingAnnual: fitAnnual,
      recommendedFederalIncomeTaxPerPaycheck: fitPerPaycheck,
      /** @deprecated Use recommendedFederalIncomeTax* — no longer includes FICA. */
      totalAnnual: fitAnnual,
      payPeriodsPerYear,
      perPaycheckTarget: fitPerPaycheck,
      perPaycheck,
      taxRates,
    };
  };

  const perSpouse = isMarried
    ? [
        buildEarnerRow({
          key: 'primary',
          title: 'Earner 1',
          subtitle: bonus > 0
            ? 'Bonus / supplemental wages count here for SS wage base and wage-share splits.'
            : null,
          incomeType: primaryType,
          grossWagesAnnual: pWages,
          seProfit: primarySeProfit,
          seDetail: primarySe,
          wageShareOfHousehold: allocFit.primaryShare,
          federalIncomeTaxAnnual: allocFit.primaryPart,
          socialSecurityAnnual: employeeSsPrimary,
          medicareAnnual: employeeMedPrimary,
          additionalMedicareAnnual: allocAddMed.primaryPart,
          payPeriodsPerYear: primaryPeriods,
        }),
        buildEarnerRow({
          key: 'spouse',
          title: 'Earner 2',
          subtitle: null,
          incomeType: spouseType,
          grossWagesAnnual: sWages,
          seProfit: spouseSeProfit,
          seDetail: spouseSe,
          wageShareOfHousehold: allocFit.spouseShare,
          federalIncomeTaxAnnual: allocFit.spousePart,
          socialSecurityAnnual: employeeSsSpouse,
          medicareAnnual: employeeMedSpouse,
          additionalMedicareAnnual: allocAddMed.spousePart,
          payPeriodsPerYear: spousePeriods,
        }),
      ]
    : [
        buildEarnerRow({
          key: 'single',
          title: 'Your estimate',
          subtitle: null,
          incomeType: primaryType,
          grossWagesAnnual: pWages,
          seProfit: primarySeProfit,
          seDetail: primarySe,
          wageShareOfHousehold: 1,
          federalIncomeTaxAnnual: federalIncomeTax,
          socialSecurityAnnual: employeeSsPrimary,
          medicareAnnual: employeeMedPrimary,
          additionalMedicareAnnual: additionalMedicareTax,
          payPeriodsPerYear: primaryPeriods,
        }),
      ];

  const suggestedQuarterly = roundCents(Math.max(0, remainingAmountToCover) / 4);
  const suggestedMonthly = roundCents(Math.max(0, remainingAmountToCover) / 12);

  return {
    taxYear: FEDERAL_PAYCHECK_TAX_CONFIG.taxYear,
    filingStatus,
    householdIncomeType,
    primaryIncomeType: primaryType,
    spouseIncomeType: isMarried ? spouseType : null,
    hasSelfEmploymentIncome: householdIncomeType !== INCOME_TYPES.W2,
    taxableIncome,
    adjustedGrossIncome,
    standardDeductionUsed: std,
    grossHouseholdIncome,
    totalGrossWagesAnnual: totalW2Wages,
    totalSeProfit,

    federalIncomeTaxAnnual: federalIncomeTax,
    socialSecurityTaxAnnual: socialSecurityTax,
    medicareTaxAnnual: medicareTax,
    additionalMedicareTaxAnnual: additionalMedicareTax,
    selfEmploymentTaxAnnual: selfEmploymentTaxOwed,
    selfEmploymentTaxDeductiblePortion: seDeductiblePortion,
    primarySelfEmployment: primarySe,
    spouseSelfEmployment: isMarried ? spouseSe : null,

    /** FIT-only — never includes SS, Medicare, or SE tax. */
    federalIncomeTaxTarget: federalIncomeTax,
    /**
     * @deprecated Name kept for analytics/compat; value is now FIT-only (not FIT+FICA).
     */
    totalAnnualFederalWithholdingTarget: federalIncomeTax,

    federalIncomeTaxWithheld: withheldFit,
    estimatedPaymentsMade: estimatedPaid,
    remainingFitToCover,
    remainingAmountToCover,
    planningSetAsideTarget,
    suggestedQuarterlySetAside: suggestedQuarterly,
    suggestedMonthlySetAside: suggestedMonthly,
    resultType,

    totalEmployeePayrollTaxes,
    totalEstimatedFederalTaxes,

    federalIncomeTaxCard: {
      estimatedFederalIncomeTax: federalIncomeTax,
      federalIncomeTaxAlreadyWithheld: withheldFit,
      estimatedPaymentsAlreadyMade: estimatedPaid,
      remainingAmountToCover: remainingFitToCover,
      projectedDueOrOverpayment: remainingFitToCover,
    },

    payrollAndSelfEmploymentCard: {
      employeeSocialSecurity: socialSecurityTax,
      employeeMedicare: medicareTax,
      additionalMedicare: additionalMedicareTax,
      totalEmployeePayrollTaxes,
      selfEmploymentSocialSecurity:
        primarySe.socialSecurityPortion + (spouseSe?.socialSecurityPortion || 0),
      selfEmploymentMedicare:
        primarySe.medicarePortion + (spouseSe?.medicarePortion || 0),
      totalSelfEmploymentTax: selfEmploymentTaxOwed,
    },

    completeFederalTaxPicture: {
      federalIncomeTax,
      employeeSocialSecurityAndMedicare: roundCents(socialSecurityTax + medicareTax),
      selfEmploymentTax: selfEmploymentTaxOwed,
      additionalMedicareTax,
      totalEstimatedFederalTaxes,
    },

    wageBaseAssumptionNote:
      'This estimate assumes the annual wages entered are subject to normal payroll-tax treatment for the full year. Year-to-date Social Security wages already paid are not collected separately.',

    perPaycheckTarget: isMarried ? null : roundCents(federalIncomeTax / primaryPeriods),
    payPeriodsPerYear: primaryPeriods,
    primaryPayPeriodsPerYear: primaryPeriods,
    spousePayPeriodsPerYear: isMarried ? spousePeriods : null,
    allocationNote: isMarried
      ? 'Joint federal income tax is allocated by each person’s share of wages (or SE profit when there are no wages). Bonus is included with Earner 1. Social Security uses each person’s own wages and remaining wage base — never a combined household wage base. Employers generally see only that job’s W-4, not the household’s full tax picture.'
      : null,
    perSpouse,
    primaryAnnualW2: primaryW2Base,
    spouseAnnualW2: sWages,
    bonusSupplemental: bonus,
    taxRates: householdTaxRates,
    incomeSummary: {
      grossWagesAnnual: totalW2Wages,
      grossHouseholdIncome,
      seProfit: totalSeProfit,
      seTaxDeduction: seDeductiblePortion,
      adjustedGrossIncome,
      standardDeductionUsed: std,
      taxableIncome,
      filingStatus,
      federalIncomeTaxAnnual: federalIncomeTax,
      marginalFederalRate: computeMarginalFederalRate(taxableIncome, filingStatus),
      federalTaxBracketBreakdown: computeFederalTaxBracketBreakdown(taxableIncome, filingStatus),
      taxLadder: buildFederalTaxLadder(taxableIncome, filingStatus, {
        earnedIncomeForSurcharge: medicareWagesCombined + totalSeNetEarnings,
      }),
    },
  };
}

/** Simplified federal income tax comparison: two singles vs one joint return. */
export function computeMarriageTaxImpact({
  primaryAnnualW2,
  spouseAnnualW2,
  bonusSupplemental = 0,
  primaryPayPeriodsPerYear = 26,
  spousePayPeriodsPerYear = 26,
  primarySeProfit = 0,
  spouseSeProfit = 0,
  primarySeDeduction = 0,
  spouseSeDeduction = 0,
}) {
  const stdSingle = FEDERAL_PAYCHECK_TAX_CONFIG.standardDeduction.single;
  const stdJoint = FEDERAL_PAYCHECK_TAX_CONFIG.standardDeduction.married_filing_jointly;

  const primaryWages = nonNeg(primaryAnnualW2) + nonNeg(bonusSupplemental);
  const spouseWages = nonNeg(spouseAnnualW2);
  const pSe = nonNeg(primarySeProfit);
  const sSe = nonNeg(spouseSeProfit);
  const pDed = nonNeg(primarySeDeduction);
  const sDed = nonNeg(spouseSeDeduction);

  const primaryAgi = Math.max(0, primaryWages + pSe - pDed);
  const spouseAgi = Math.max(0, spouseWages + sSe - sDed);

  const primarySingleTax = computeFederalIncomeTax(Math.max(0, primaryAgi - stdSingle), 'single');
  const spouseSingleTax = computeFederalIncomeTax(Math.max(0, spouseAgi - stdSingle), 'single');
  const combinedSingleTax = roundCents(primarySingleTax + spouseSingleTax);

  const jointAgi = Math.max(0, primaryWages + spouseWages + pSe + sSe - pDed - sDed);
  const jointTaxable = Math.max(0, jointAgi - stdJoint);
  const jointTax = computeFederalIncomeTax(jointTaxable, 'married_filing_jointly');
  const difference = roundCents(jointTax - combinedSingleTax);

  const threshold = Math.max(500, jointTax * 0.02);
  let outcome = 'neutral';
  if (difference > threshold) outcome = 'penalty';
  else if (difference < -threshold) outcome = 'bonus';

  const outcomeLabel = outcome === 'penalty'
    ? '⚠ Marriage Penalty'
    : outcome === 'bonus'
      ? '✓ Marriage Bonus'
      : 'No Meaningful Difference';

  const buildPerPaycheck = (periods) => {
    const n = Math.max(1, Number(periods) || 1);
    return {
      combinedSingleTax: roundCents(combinedSingleTax / n),
      jointTax: roundCents(jointTax / n),
      difference: roundCents(difference / n),
      payPeriodsPerYear: n,
    };
  };

  const primaryPeriods = Math.max(1, Number(primaryPayPeriodsPerYear) || 1);
  const spousePeriods = Math.max(1, Number(spousePayPeriodsPerYear ?? primaryPayPeriodsPerYear) || 1);

  return {
    combinedSingleTax,
    jointTax,
    difference,
    outcome,
    outcomeLabel,
    perPaycheck: {
      primary: buildPerPaycheck(primaryPeriods),
      spouse: buildPerPaycheck(spousePeriods),
    },
  };
}

export function computeTaxComplexityAssessment({
  filingStatus,
  primaryAnnualW2,
  spouseAnnualW2,
  bonusSupplemental,
  additionalMedicareTaxAnnual,
  complexityFlags = {},
  hasSelfEmploymentIncome = false,
}) {
  const factors = [];

  if (filingStatus === 'married_filing_jointly') {
    factors.push('Married filing jointly');
  }
  if (
    filingStatus === 'married_filing_jointly'
    && (Number(primaryAnnualW2) || 0) > 0
    && (Number(spouseAnnualW2) || 0) > 0
  ) {
    factors.push('Dual income household');
  }
  if ((Number(bonusSupplemental) || 0) > 0) {
    factors.push('Bonus / supplemental pay');
  }
  if ((Number(additionalMedicareTaxAnnual) || 0) > 0) {
    factors.push('Additional Medicare Tax');
  }
  if (complexityFlags.rsus) factors.push('RSUs / equity compensation');
  if (complexityFlags.rentalIncome) factors.push('Rental income');
  if (complexityFlags.selfEmployment || hasSelfEmploymentIncome) {
    factors.push('Self-employment income');
  }
  if (complexityFlags.itemizing) factors.push('Itemized deductions');
  if (complexityFlags.estimatedPayments) factors.push('Estimated tax payments');

  const count = factors.length;
  let level = 'simple';
  let stars = 1;
  if (count >= 5) {
    level = 'advanced';
    stars = 5;
  } else if (count >= 3) {
    level = 'complex';
    stars = 4;
  } else if (count === 2) {
    level = 'moderate';
    stars = 2;
  } else if (count <= 1) {
    level = 'simple';
    stars = 1;
  }

  const levelLabel = level.charAt(0).toUpperCase() + level.slice(1);

  return {
    factors,
    count,
    level,
    levelLabel,
    stars,
    summary: count >= 3
      ? 'Your finances have become more complex than a standard W-4 can typically handle.'
      : count === 2
        ? 'Your household may be moving beyond a simple paycheck withholding setup.'
        : 'Your current inputs suggest a relatively straightforward tax picture — though real life is often more nuanced.',
  };
}

const PLANNING_OUTLOOKS = {
  may_over: {
    headline: 'May Be Overwithholding',
    body: 'Based on the federal income tax withheld you entered, you may be covering more than this simplified federal income-tax estimate suggests. That isn\'t necessarily bad — but it may mean more income tax is being withheld during the year than needed under these assumptions. Social Security and Medicare are separate payroll taxes already taken from W-2 paychecks.',
    opportunities: [
      'Compare the “Federal income tax” paystub line to this income-tax target',
      'Review W-4 elections',
      'Evaluate retirement contributions and take-home pay',
    ],
    className: 'trc-outlook--refund',
  },
  likely_enough: {
    headline: 'Likely Withholding Enough',
    body: 'Based on the federal income tax withheld you entered, your current income-tax withholding appears reasonably close to this simplified federal income-tax estimate.',
    opportunities: [
      'Review W-4 elections after major life changes',
      'Coordinate household withholding if dual income',
      'Monitor after bonuses or equity compensation',
    ],
    className: 'trc-outlook--balanced',
  },
  may_under: {
    headline: 'May Be Underwithholding',
    body: 'Based on the federal income tax withheld you entered, you may be covering less than this simplified federal income-tax estimate suggests. That can lead to a balance due at tax time if the pattern continues. This comparison does not include Social Security or Medicare — those are separate payroll-tax lines.',
    opportunities: [
      'Review W-4 elections',
      'Estimate quarterly taxes if you have 1099 income',
      'Coordinate household withholding across employers',
    ],
    className: 'trc-outlook--due',
  },
};

export function buildWithholdingComparison(estimatedTarget, statedWithholding) {
  if (statedWithholding == null || !Number.isFinite(statedWithholding)) return null;

  const gap = roundCents(statedWithholding - estimatedTarget);
  const pct = estimatedTarget > 0 ? Math.abs(gap) / estimatedTarget : (Math.abs(gap) > 0 ? 1 : 0);

  let status = 'likely_enough';
  let label = 'Likely withholding enough';
  if (gap > 500 || (gap > 0 && pct > 0.05)) {
    status = 'may_over';
    label = 'May be overwithholding';
  } else if (gap < -500 || (gap < 0 && pct > 0.05)) {
    status = 'may_under';
    label = 'May be underwithholding';
  }

  return {
    gap,
    status,
    label,
    statedWithholding,
    estimatedTarget,
  };
}

export function buildTaxPlanningOutlook({
  estimatedLiability,
  statedWithholding,
}) {
  if (statedWithholding == null || !Number.isFinite(statedWithholding)) {
    return null;
  }

  const comparison = buildWithholdingComparison(estimatedLiability, statedWithholding);
  if (!comparison) return null;

  if (comparison.status === 'may_over') return PLANNING_OUTLOOKS.may_over;
  if (comparison.status === 'may_under') return PLANNING_OUTLOOKS.may_under;
  return PLANNING_OUTLOOKS.likely_enough;
}

const SITUATION_CARD_COPY = {
  marriage: {
    title: 'Marriage',
    body: 'Marriage can change tax brackets, deductions, and withholding needs depending on both spouses\' incomes.',
  },
  dualIncome: {
    title: 'Dual Income Household',
    body: 'Two paychecks don\'t communicate with each other. Each employer withholds independently, while the IRS looks at your combined income.',
  },
  differentPayFrequencies: {
    title: 'Different Pay Frequencies',
    body: 'Weekly and biweekly payroll schedules can make withholding harder to estimate across a household.',
  },
  bonus: {
    single: {
      title: 'Bonus Income',
      body: 'Bonuses are often withheld using different payroll methods than regular wages, which can cause your year-end tax result to differ from your paycheck.',
    },
    married: {
      title: 'Bonus Income',
      body: 'Household bonuses often increase the gap between payroll withholding and actual taxes owed.',
    },
  },
  rsus: {
    single: {
      title: 'RSUs',
      body: 'Equity compensation often creates tax situations that a standard W-4 isn\'t designed to handle.',
    },
    married: {
      title: 'RSUs',
      body: 'Equity compensation at the household level often creates tax situations that standard W-4 elections aren\'t designed to handle.',
    },
  },
  selfEmployment: {
    title: 'Self Employment',
    body: '1099 income usually does not have taxes automatically removed. Plan for federal income tax and self-employment tax separately from W-2 paycheck withholding.',
  },
  rentalIncome: {
    single: {
      title: 'Rental Income',
      body: 'Rental income isn\'t captured on a W-2 paycheck, so payroll withholding may not reflect your full tax picture.',
    },
    married: {
      title: 'Rental Income',
      body: 'Rental income adds household complexity that payroll withholding alone typically cannot address.',
    },
  },
  itemizing: {
    title: 'Itemized Deductions',
    body: 'Itemizing can change your effective tax rate in ways a standard W-4 withholding calculation may not reflect.',
  },
  estimatedPayments: {
    single: {
      title: 'Estimated Tax Payments',
      body: 'Estimated payments usually signal that paycheck withholding alone isn\'t covering your full tax obligation.',
    },
    married: {
      title: 'Estimated Tax Payments',
      body: 'Estimated payments often mean household taxes extend beyond what payroll withholding can handle.',
    },
  },
};

const HIGH_INCOME_HOUSEHOLD_THRESHOLD = 200_000;

export function buildTaxReadinessPlanningContent({
  estimate,
  complexityFlags = {},
  complexity,
}) {
  const isMarried = estimate.filingStatus === 'married_filing_jointly';
  const hasBonus = (Number(estimate.bonusSupplemental) || 0) > 0;
  const dualIncome = isMarried
    && (
      (Number(estimate.primaryAnnualW2) || 0) > 0
      || (Number(estimate.primarySelfEmployment?.netProfit) || 0) > 0
    )
    && (
      (Number(estimate.spouseAnnualW2) || 0) > 0
      || (Number(estimate.spouseSelfEmployment?.netProfit) || 0) > 0
    );
  const differentPayFrequencies = isMarried
    && estimate.primaryPayPeriodsPerYear !== estimate.spousePayPeriodsPerYear;
  const householdIncome = Number(estimate.grossHouseholdIncome)
    || (
      (Number(estimate.primaryAnnualW2) || 0)
      + (Number(estimate.spouseAnnualW2) || 0)
      + (Number(estimate.bonusSupplemental) || 0)
    );
  const isHighIncome = householdIncome >= HIGH_INCOME_HOUSEHOLD_THRESHOLD
    || (Number(estimate.additionalMedicareTaxAnnual) || 0) > 0
    || complexity?.level === 'advanced';
  const hasSe = estimate.hasSelfEmploymentIncome
    || complexityFlags.selfEmployment;

  const situationCards = [];
  const pick = (key) => (isMarried && SITUATION_CARD_COPY[key]?.married
    ? SITUATION_CARD_COPY[key].married
    : SITUATION_CARD_COPY[key].single || SITUATION_CARD_COPY[key]);

  if (isMarried) {
    situationCards.push({ key: 'marriage', ...SITUATION_CARD_COPY.marriage });
    if (dualIncome) {
      situationCards.push({ key: 'dualIncome', ...SITUATION_CARD_COPY.dualIncome });
    }
    if (differentPayFrequencies) {
      situationCards.push({ key: 'differentPayFrequencies', ...SITUATION_CARD_COPY.differentPayFrequencies });
    }
  }

  if (hasBonus) situationCards.push({ key: 'bonus', ...pick('bonus') });
  if (complexityFlags.rsus) situationCards.push({ key: 'rsus', ...pick('rsus') });
  if (hasSe) situationCards.push({ key: 'selfEmployment', ...SITUATION_CARD_COPY.selfEmployment });
  if (complexityFlags.rentalIncome) situationCards.push({ key: 'rentalIncome', ...pick('rentalIncome') });
  if (complexityFlags.itemizing) situationCards.push({ key: 'itemizing', ...SITUATION_CARD_COPY.itemizing });
  if (complexityFlags.estimatedPayments || (Number(estimate.estimatedPaymentsMade) || 0) > 0) {
    situationCards.push({ key: 'estimatedPayments', ...pick('estimatedPayments') });
  }

  const planningTopics = [];
  const addTopic = (key, label) => {
    if (!planningTopics.some((t) => t.key === key)) {
      planningTopics.push({ key, label });
    }
  };

  addTopic('w4Review', 'W-4 review');
  if (isMarried) addTopic('householdWithholding', 'Household withholding');
  if (isMarried && marriageTaxTopicRelevant(estimate)) {
    addTopic('marriageTaxImpact', 'Marriage tax impact');
  }
  if (hasBonus) addTopic('bonusWithholding', 'Bonus withholding');
  if (complexityFlags.rsus) addTopic('equityCompensation', 'Equity compensation withholding');
  if (hasSe || complexityFlags.estimatedPayments) {
    addTopic('estimatedTaxes', 'Estimated taxes');
  }
  if ((Number(estimate.additionalMedicareTaxAnnual) || 0) > 0 || isHighIncome) {
    addTopic('medicareThresholds', 'Medicare thresholds');
  }
  if (isHighIncome || (complexity?.count ?? 0) >= 2) {
    addTopic('rothContributions', 'Roth contributions');
  }
  addTopic('retirementSavings', 'Retirement savings');

  let nextSteps;
  if (isHighIncome) {
    nextSteps = [
      { label: 'Roth conversion opportunities', href: '/roth-vs-traditional-calculator' },
      { label: 'Additional Medicare Tax' },
      { label: 'Investment tax planning' },
      { label: 'Retirement income strategy', href: '/retirement-gap-estimator' },
      { label: 'Estate planning' },
    ];
  } else if (isMarried) {
    nextSteps = [
      { label: 'Household withholding' },
      { label: 'Roth vs Traditional', href: '/roth-vs-traditional-calculator' },
      { label: 'Social Security', href: '/social-security-estimator' },
      { label: 'Retirement income planning', href: '/retirement-gap-estimator' },
    ];
  } else {
    nextSteps = [
      { label: 'W-4 review' },
      { label: 'Emergency fund' },
      { label: 'Retirement contributions', href: '/retirement-gap-estimator' },
    ];
  }

  return {
    sectionTitle: isMarried
      ? 'Why Household Taxes Become More Complex'
      : 'Why Your Paycheck May No Longer Match Reality',
    sectionIntro: isMarried
      ? 'When two incomes combine, paycheck withholding often stops matching how the IRS views your household.'
      : 'As your situation grows beyond basic W-2 wages, a standard paycheck may stop reflecting your full tax picture.',
    situationCards,
    planningTopics,
    nextSteps,
    isHighIncome,
    isMarried,
  };
}

function marriageTaxTopicRelevant(estimate) {
  return (Number(estimate.spouseAnnualW2) || 0) > 0
    || (Number(estimate.spouseSelfEmployment?.netProfit) || 0) > 0;
}

export function computeTaxReadinessAnalysis({
  estimate,
  complexityFlags,
  statedWithholding,
}) {
  const marriage = estimate.filingStatus === 'married_filing_jointly'
    ? computeMarriageTaxImpact({
      primaryAnnualW2: estimate.primaryAnnualW2,
      spouseAnnualW2: estimate.spouseAnnualW2,
      bonusSupplemental: estimate.bonusSupplemental,
      primaryPayPeriodsPerYear: estimate.primaryPayPeriodsPerYear,
      spousePayPeriodsPerYear: estimate.spousePayPeriodsPerYear,
      primarySeProfit: estimate.primarySelfEmployment?.netProfit || 0,
      spouseSeProfit: estimate.spouseSelfEmployment?.netProfit || 0,
      primarySeDeduction: estimate.primarySelfEmployment?.deductiblePortion || 0,
      spouseSeDeduction: estimate.spouseSelfEmployment?.deductiblePortion || 0,
    })
    : null;

  const complexity = computeTaxComplexityAssessment({
    filingStatus: estimate.filingStatus,
    primaryAnnualW2: estimate.primaryAnnualW2,
    spouseAnnualW2: estimate.spouseAnnualW2,
    bonusSupplemental: estimate.bonusSupplemental,
    additionalMedicareTaxAnnual: estimate.additionalMedicareTaxAnnual,
    complexityFlags,
    hasSelfEmploymentIncome: estimate.hasSelfEmploymentIncome,
  });

  /** Compare stated FIT withholding to FIT-only target (never FICA/SE). */
  const fitTarget = estimate.federalIncomeTaxTarget ?? estimate.totalAnnualFederalWithholdingTarget;

  const outlook = buildTaxPlanningOutlook({
    estimatedLiability: fitTarget,
    statedWithholding,
  });

  const withholdingComparison = buildWithholdingComparison(fitTarget, statedWithholding);

  const planning = buildTaxReadinessPlanningContent({
    estimate,
    complexityFlags,
    complexity,
  });

  return {
    marriage,
    complexity,
    outlook,
    planning,
    hasStatedWithholding: statedWithholding != null && Number.isFinite(statedWithholding),
    statedWithholding,
    withholdingComparison,
    resultType: estimate.resultType,
  };
}

/** Effective and statutory rates for one earner or household. */
export function computeEarnerTaxRates({
  grossWagesAnnual,
  taxableIncomeAnnual = null,
  federalIncomeTaxAnnual,
  socialSecurityAnnual,
  medicareAnnual,
  additionalMedicareAnnual = 0,
  selfEmploymentTaxAnnual = 0,
  filingStatus = 'single',
}) {
  const wages = nonNeg(grossWagesAnnual);
  const taxable = nonNeg(taxableIncomeAnnual);
  const fit = nonNeg(federalIncomeTaxAnnual);
  const ss = nonNeg(socialSecurityAnnual);
  const med = nonNeg(medicareAnnual);
  const addMed = nonNeg(additionalMedicareAnnual);
  const seTax = nonNeg(selfEmploymentTaxAnnual);
  const medicareTotal = med + addMed;

  const { socialSecurity: ssConfig, medicare: medConfig } = FEDERAL_PAYCHECK_TAX_CONFIG;
  const statutorySocialSecurityRate = ssConfig.employeeRate;
  const statutoryMedicareRate = medConfig.employeeRate;
  const statutoryAdditionalMedicareRate = medConfig.additionalRate;

  const rateOnGross = (tax) => (wages > 0 ? tax / wages : 0);
  const effectiveFederalRateOnGross = rateOnGross(fit);
  const effectiveFederalRateOnTaxable = taxable > 0 ? fit / taxable : effectiveFederalRateOnGross;

  return {
    effectiveFederalRate: effectiveFederalRateOnTaxable,
    effectiveFederalRateOnGross,
    effectiveFederalRateOnTaxable,
    marginalFederalRate: taxable > 0 ? computeMarginalFederalRate(taxable, filingStatus) : null,
    effectiveSocialSecurityRate: rateOnGross(ss),
    effectiveMedicareRate: rateOnGross(medicareTotal),
    effectiveRegularMedicareRate: rateOnGross(med),
    effectiveAdditionalMedicareRate: rateOnGross(addMed),
    /** FIT + employee payroll taxes as % of gross (explicitly not “effective income-tax rate”). */
    effectiveFederalIncomeAndEmployeePayrollRate: rateOnGross(fit + ss + medicareTotal),
    /** @deprecated Misnamed; prefer effectiveFederalIncomeAndEmployeePayrollRate. */
    effectiveCombinedPayrollRate: rateOnGross(fit + ss + medicareTotal),
    effectiveFederalIncomeAndAllPayrollRate: rateOnGross(fit + ss + medicareTotal + seTax),
    statutorySocialSecurityRate,
    statutoryMedicareRate,
    statutoryAdditionalMedicareRate,
    statutorySelfEmploymentCombinedRate:
      ssConfig.selfEmploymentRate + medConfig.selfEmploymentRate,
    socialSecurityWageBase: ssConfig.annualWageBase,
    socialSecurityAtWageCap: wages > ssConfig.annualWageBase,
    hasAdditionalMedicare: addMed > 0,
    taxableIncomeAnnual: taxable > 0 ? taxable : null,
    grossWagesAnnual: wages,
    filingStatus,
  };
}

/** Per-paycheck federal income tax vs FICA for one earner row. */
export function computeEarnerPerPaycheckBreakdown({
  federalIncomeTaxAnnual,
  socialSecurityAnnual,
  medicareAnnual,
  additionalMedicareAnnual = 0,
  payPeriodsPerYear,
}) {
  const periods = Math.max(1, Number(payPeriodsPerYear) || 1);
  const ficaTaxAnnual = roundCents(
    nonNeg(socialSecurityAnnual)
    + nonNeg(medicareAnnual)
    + nonNeg(additionalMedicareAnnual),
  );

  return {
    federalIncomeTax: roundCents(nonNeg(federalIncomeTaxAnnual) / periods),
    fica: roundCents(ficaTaxAnnual / periods),
    socialSecurity: roundCents(nonNeg(socialSecurityAnnual) / periods),
    medicare: roundCents(
      (nonNeg(medicareAnnual) + nonNeg(additionalMedicareAnnual)) / periods,
    ),
    ficaTaxAnnual,
    payPeriodsPerYear: periods,
  };
}
